/** @jsx jsx */
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import { Fragment } from "react"
import { jsx } from "theme-ui"

import Border from "@components/Border"
import Media from "@components/Media"
import Variant from "@components/Variant"

import List from "./List"

export const fragment = graphql`
  fragment ContentfulFeatureBlock on ContentfulFeatureBlock {
    id
    layout
    link {
      href
      slug
      title
    }
    list {
      __typename
      ... on ContentfulIconListItem {
        icon {
          altText: title
          file {
            url
          }
        }
        id
        text {
          text
        }
        title
      }
      ... on ContentfulSimpleListItem {
        id
        simpleText: text
      }
    }
    listTitle
    media {
      ...Media
    }
    appStoreLinks {
      url
      image {
        ...ImageFields
      }
      altText
    }
    subtitle
    text {
      json
    }
    title
    backgroundColor
    imagePlacement
  }
`

export default function Features({
  layout,
  link,
  list,
  listTitle,
  media,
  subtitle,
  title,
  text,
  backgroundColor,
  appStoreLinks,
  imagePlacement,
}) {
  const imgLeft = layout === "Image left"
  const imgBottom = imagePlacement === "bottom"
  const white = "255,255,255,1"
  const isColoredBackground = backgroundColor !== white

  return (
    <Fragment>
      <Variant
        as="section"
        sx={{
          backgroundColor: `rgba(${backgroundColor})`,
          borderRadius: "30px",
        }}
        variants={["sectionContainer"]}
      >
        <Variant
          as="div"
          columns={["1fr", "1fr", "1fr 1fr"]}
          d="grid"
          gap={[10, 10, 14]}
          sx={{
            "> .image-container": {
              order: [imgBottom ? 1 : 0, imgBottom ? 1 : 0, imgLeft ? 0 : 1],
            },
          }}
          variants={["container", "containerSpacingX"]}
        >
          <div
            className="image-container"
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              paddingTop: [0, 0, isColoredBackground ? 16 : 0],
              paddingBottom: imgBottom
                ? 0
                : [isColoredBackground ? 15 : 0, isColoredBackground ? 16 : 0],
            }}
          >
            <div
              sx={{
                height: "100%",
                width: "100%",
                maxWidth: 460,
                display: "flex",
                justifyContent: "center",
                alignItems: imgBottom ? "end" : "center",
              }}
            >
              <Media media={media} />
            </div>
          </div>
          <div
            className="text-container"
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              paddingTop: [
                isColoredBackground ? 10 : 0,
                isColoredBackground ? 13 : 0,
                isColoredBackground ? 16 : 0,
              ],
              paddingBottom: [
                0,
                isColoredBackground ? 13 : 0,
                isColoredBackground ? 16 : 0,
              ],
            }}
          >
            <List
              link={link}
              list={list}
              listTitle={listTitle}
              subtitle={subtitle}
              text={text}
              title={title}
              appStoreLinks={appStoreLinks}
            />
          </div>
        </Variant>
      </Variant>
      <Border />
    </Fragment>
  )
}

Features.typeName = "ContentfulFeatureBlock"

Features.defaultProps = {
  link: null,
  listTitle: undefined,
  subtitle: undefined,
  text: null,
}

Features.propTypes = {
  layout: PropTypes.string.isRequired,
  link: PropTypes.shape({
    href: PropTypes.string,
    slug: PropTypes.string,
    title: PropTypes.string.isRequired,
  }),
  list: PropTypes.array.isRequired,
  listTitle: PropTypes.string,
  media: PropTypes.object.isRequired,
  subtitle: PropTypes.string,
  text: PropTypes.shape({
    json: PropTypes.object.isRequired,
  }),
  title: PropTypes.string.isRequired,
}
